"use client";

import {
  useFloating,
  offset,
  shift,
  flip,
  autoUpdate,
  arrow,
  FloatingArrow,
  Placement,
} from "@floating-ui/react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  Card,
  CardHeader,
  CardContent,
  CardTitle,
  CardFooter,
  CardAction,
  Close,
  DialogDescription,
  useStep,
  DialogTrigger,
} from "@oreme/ui";
import { useAtomValue, useAtom, SetStateAction } from "jotai";
import { useTranslations } from "next-intl";
import React, { type ReactElement, useEffect, useState, useMemo } from "react";
import { useSite } from "src/hooks/sites";
import {
  sitesAtom,
  selectedSiteAtom,
  sidebarVisibilityAtom,
  sidebarFilterTagVisibilityAtom,
  sidebarFilterObservatoryVisibilityAtom,
  sidebarFilterLocationVisibilityAtom,
  tourVisibilityAtom,
  settingsVisibilityAtom,
} from "@/store/sites";
import { Site } from "@/types";

interface BeforeEnterProps {
  setSelectedSite: (site: SetStateAction<Site | null>) => void;
  sites: Site[];
  locate: (site: Site, zoom?: number) => void;
  isSidebarVisible: boolean;
  toggleSidebarVisibility: (v: boolean) => void;
  isSidebarTagVisible: boolean;
  toggleSidebarTagVisibility: (v: boolean) => void;
  isSidebarObservatoryVisible: boolean;
  toggleSidebarObservatoryVisibility: (v: boolean) => void;
  isSidebarLocationVisible: boolean;
  toggleSidebarLocationVisibility: (v: boolean) => void;
  isSettingsVisible: boolean;
  toggleSettingsVisibility: (v: boolean) => void;
}

interface TourStep {
  elementId: string;
  title: string;
  description: string;
  action: string;
  placement: Placement;
  offset?: number;
  beforeEnter?: (props: BeforeEnterProps) => Promise<void> | void;
}

const steps: TourStep[] = [
  {
    elementId: "tour-1",
    title: "step1.title",
    description: "step1.description",
    action: "step1.action",
    placement: "right",
    beforeEnter: async ({ toggleSettingsVisibility }) => {
      toggleSettingsVisibility(false);
    },
  },
  {
    elementId: "tour-2",
    title: "step2.title",
    description: "step2.description",
    action: "step2.action",
    placement: "right",
    beforeEnter: async ({ setSelectedSite, sites, locate }) => {
      if (sites.length > 0) {
        const site = sites.find((site) => site.id === "snokarst-241");
        if (site) {
          setSelectedSite(site);
          locate(site, 12);
        }
      }
    },
  },
  {
    elementId: "tour-3", // Tag
    title: "step3.title",
    description: "step3.description",
    action: "step3.action",
    placement: "right",
    offset: 24,
    beforeEnter: async ({
      setSelectedSite,
      toggleSidebarVisibility,
      toggleSidebarObservatoryVisibility,
      toggleSidebarTagVisibility,
      toggleSidebarLocationVisibility,
    }) => {
      setSelectedSite(null);
      toggleSidebarVisibility(true);
      toggleSidebarObservatoryVisibility(false);
      toggleSidebarTagVisibility(true);
      toggleSidebarLocationVisibility(false);
    },
  },
  {
    elementId: "tour-4", // Observatory
    title: "step4.title",
    description: "step4.description",
    action: "step4.action",
    placement: "right",
    offset: 24,
    beforeEnter: async ({
      toggleSidebarVisibility,
      toggleSidebarTagVisibility,
      toggleSidebarObservatoryVisibility,
      toggleSidebarLocationVisibility,
    }) => {
      toggleSidebarVisibility(true);
      toggleSidebarTagVisibility(false);
      toggleSidebarObservatoryVisibility(true);
      toggleSidebarLocationVisibility(false);
    },
  },
  {
    elementId: "tour-5", // Location
    title: "step5.title",
    description: "step5.description",
    action: "step5.action",
    placement: "right",
    offset: 24,
    beforeEnter: async ({
      toggleSidebarVisibility,
      toggleSidebarTagVisibility,
      toggleSidebarObservatoryVisibility,
      toggleSidebarLocationVisibility,
    }) => {
      toggleSidebarVisibility(true);
      toggleSidebarTagVisibility(false);
      toggleSidebarObservatoryVisibility(false);
      toggleSidebarLocationVisibility(true);
    },
  },
  {
    elementId: "tour-6",
    title: "step6.title",
    description: "step6.description",
    action: "step6.action",
    placement: "right",
    beforeEnter: async ({
      toggleSidebarVisibility,
      toggleSidebarTagVisibility,
      toggleSidebarLocationVisibility,
      toggleSettingsVisibility,
    }) => {
      toggleSidebarVisibility(true);
      toggleSidebarTagVisibility(false);
      toggleSidebarLocationVisibility(false);
      toggleSettingsVisibility(true);
    },
  },
  {
    elementId: "tour-7",
    title: "step7.title",
    description: "step7.description",
    action: "step7.action",
    placement: "left",
    beforeEnter: async ({
      toggleSidebarVisibility,
      toggleSidebarTagVisibility,
      toggleSidebarLocationVisibility,
      toggleSettingsVisibility,
    }) => {
      toggleSidebarVisibility(true);
      toggleSidebarTagVisibility(false);
      toggleSidebarLocationVisibility(false);
      toggleSettingsVisibility(false);
    },
  },
];

function SitesTour(): ReactElement | null {
  const t = useTranslations("sites.Tour");
  const { locate } = useSite();

  const [currentStep, stepActions] = useStep(steps.length);
  const step = steps[currentStep - 1];
  const placement = step?.placement ?? "right";
  const currentOffset = step?.offset ?? 16;
  const [arrowEl, setArrowEl] = useState<SVGSVGElement | null>(null);
  const [referenceEl, setReferenceEl] = useState<HTMLElement | null>(null);
  const [floatingEl, setFloatingEl] = useState<HTMLDivElement | null>(null);

  const middleware = useMemo(
    () => [offset(currentOffset), flip(), shift(), arrow({ element: arrowEl })],
    [currentOffset, arrowEl],
  );

  // Atoms
  const sites = useAtomValue(sitesAtom);
  const [, setSelectedSite] = useAtom(selectedSiteAtom);
  const [isSidebarVisible, toggleSidebarVisibility] = useAtom(
    sidebarVisibilityAtom,
  );
  const [isSidebarTagVisible, toggleSidebarTagVisibility] = useAtom(
    sidebarFilterTagVisibilityAtom,
  );
  const [isSidebarObservatoryVisible, toggleSidebarObservatoryVisibility] =
    useAtom(sidebarFilterObservatoryVisibilityAtom);
  const [isSidebarLocationVisible, toggleSidebarLocationVisibility] = useAtom(
    sidebarFilterLocationVisibilityAtom,
  );
  const [isSettingsVisible, toggleSettingsVisibility] = useAtom(
    settingsVisibilityAtom,
  );
  const [isTourVisible, setTourVisibility] = useAtom(tourVisibilityAtom);

  // Tour state
  const [isTourStarted, setTourStarted] = useState(false);

  // Stable floating hook
  const { floatingStyles, context, update } = useFloating({
    placement,
    middleware,
    whileElementsMounted: autoUpdate,
    elements: {
      reference: referenceEl,
      floating: floatingEl,
    },
  });

  /* ------------------------------------------
     1. Run beforeEnter when step changes
  -------------------------------------------*/
  useEffect(() => {
    if (!isTourStarted || !step) return;

    (async () => {
      if (step.beforeEnter) {
        await step.beforeEnter({
          setSelectedSite,
          sites,
          locate,
          isSidebarVisible,
          toggleSidebarVisibility,
          isSidebarTagVisible,
          toggleSidebarTagVisibility,
          isSidebarObservatoryVisible,
          toggleSidebarObservatoryVisibility,
          isSidebarLocationVisible,
          toggleSidebarLocationVisibility,
          isSettingsVisible,
          toggleSettingsVisibility,
        });
      }
    })();
  }, [
    isTourStarted,
    currentStep,
    step,
    setSelectedSite,
    sites,
    locate,
    isSidebarVisible,
    toggleSidebarVisibility,
    isSidebarTagVisible,
    isSidebarObservatoryVisible,
    toggleSidebarObservatoryVisibility,
    toggleSidebarTagVisibility,
    isSidebarLocationVisible,
    toggleSidebarLocationVisibility,
    isSettingsVisible,
    toggleSettingsVisibility,
  ]);

  /* ------------------------------------------
     2. Attach floating tooltip after the element exists
  -------------------------------------------*/
  useEffect(() => {
    if (!step || !isTourStarted) return;

    // Wait for the element to exist in DOM
    const interval = setInterval(() => {
      const el = document.getElementById(step.elementId);
      if (el) {
        setReferenceEl(el);
        update?.();
        clearInterval(interval);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [step.elementId, isTourStarted, setReferenceEl, update, step]);

  /* ------------------------------------------
     3. Update position
  -------------------------------------------*/
  useEffect(() => {
    if (!step) return;
    update?.();
  }, [step, update]);

  const handleClick = () => {
    if (stepActions.canGoToNextStep) {
      stepActions.goToNextStep();
    } else {
      handleExit();
    }
  };

  const handleStart = () => {
    setTourVisibility(false);
    setTourStarted(true);
  };

  const handleExit = () => {
    setTourVisibility(false);
    setTourStarted(false);
    stepActions.setStep(1);
  };

  /* ------------------------------
     Render onboarding dialog
  -------------------------------*/
  if (isTourVisible) {
    return (
      <Dialog open={isTourVisible} onOpenChange={setTourVisibility}>
        <DialogTrigger />
        <DialogContent>
          <DialogTitle>{t("onboarding.title")}</DialogTitle>
          <DialogDescription>{t("onboarding.description")}</DialogDescription>
          <DialogFooter>
            <Button onClick={handleStart}>{t("onboarding.action")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  /* ------------------------------
     Render floating tooltip
  -------------------------------*/
  if (!isTourStarted || !step) return null;

  return (
    <div
      ref={setFloatingEl}
      style={{
        ...floatingStyles,
        zIndex: 9999,
      }}
      className="w-72 shadow-lg rounded-xl p-0"
    >
      <Card className="gap-1 border-0">
        <CardHeader>
          <CardTitle className="text-sm">{t(step.title)}</CardTitle>
          <CardAction>
            <Close onClick={handleExit} />
          </CardAction>
        </CardHeader>
        <CardContent className="text-xs">{t(step.description)}</CardContent>
        <CardFooter className="flex flex-row justify-between items-center px-3 py-2 m-0 mt-2">
          <span className="text-xs">{`${currentStep}/${steps.length}`}</span>
          <Button size="sm" onClick={handleClick}>
            {t(step.action)}
          </Button>
        </CardFooter>
      </Card>
      <FloatingArrow
        ref={setArrowEl}
        context={context}
        className="fill-background"
      />
    </div>
  );
}

export { SitesTour };
